import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  DateField,
  Field,
  Modal,
  ModalActions,
  Select,
} from '@evogard/ui';
import { format, formatPercentage, fromReais } from '@evogard/domain';
import type { ConsultantSummary, OwnAwardGrants } from '@evogard/contract';
import { api } from '../../lib/api.js';
import { refusalOf } from './common.js';

type OwnAward = OwnAwardGrants['awards'][number];

/** A chave da consulta que o Painel do time lê — invalidada a cada gravação. */
export const OWN_AWARD_GRANTS_KEY = ['comercial', 'minhas-concessoes'] as const;

/**
 * Repartir a própria premiação de líder com UM liderado, a partir do Painel do time.
 *
 * ## Em modal, e por quê
 *
 * É parte de uma LINHA da tabela, o formulário tem dois campos (percentual e
 * ponta do contrato) e não há ficha do liderado para onde levar o líder — é a
 * mesma exceção de `DESIGN.md` que `UserPermissionExceptionsModal` usa.
 *
 * ## O campo é uma FATIA da premiação do líder — desde 23/09/2026
 *
 * "Ele fica com 20%, eu fico com 80%": a bonificação do líder sobre a carteira
 * do liderado é o todo, e o líder diz quanto dela fica com o liderado. O resto
 * é a subtração, e o servidor nunca o recebe — dois números digitados
 * deixariam a soma deixar de ser 100.
 *
 * ## Arquivada não é "sem concessão"
 *
 * A unique `(award_id, member_consultant_id)` inclui a linha arquivada, então
 * criar de novo recusaria. A concessão arquivada aparece com o botão de
 * reativar, e o formulário só volta depois dele: reativar e editar numa
 * gravação só seriam duas chamadas, e a primeira sozinha religaria o
 * percentual antigo sem ninguém ter escolhido.
 */
export function GrantAwardModal({
  member,
  mine,
  onClose,
}: {
  member: ConsultantSummary;
  mine: OwnAwardGrants;
  onClose: () => void;
}) {
  const [awardId, setAwardId] = useState(mine.awards[0]?.id ?? 0);
  const award = mine.awards.find((a) => a.id === awardId);

  return (
    <Modal
      open
      onOpenChange={(next) => !next && onClose()}
      title={`Repartir premiação com ${member.user.nome}`}
      descricao="A sua bonificação sobre as vendas desta pessoa é o todo: você diz quanto dela fica com ela, e o resto fica com você."
    >
      <div className="flex flex-col gap-5">
        {/*
         * O seletor só aparece com mais de uma premiação de líder — com uma
         * só, é a tela perguntando o que já está respondido.
         */}
        {mine.awards.length > 1 && (
          <Select
            label="Premiação"
            value={String(awardId)}
            onChange={(e) => setAwardId(Number(e.target.value))}
          >
            {mine.awards.map((a) => (
              <option key={a.id} value={a.id}>
                {a.title} — {formatPercentage(a.percentage)}%
              </option>
            ))}
          </Select>
        )}

        {award && (
          /* A chave refaz o estado do formulário ao trocar de premiação. */
          <GrantForm key={award.id} award={award} member={member} onClose={onClose} />
        )}
      </div>
    </Modal>
  );
}

function GrantForm({
  award,
  member,
  onClose,
}: {
  award: OwnAward;
  member: ConsultantSummary;
  onClose: () => void;
}) {
  const client = useQueryClient();
  const existing = award.grants.find((g) => g.memberConsultantId === member.id);
  const [percentage, setPercentage] = useState(
    existing ? formatPercentage(existing.percentage) : '',
  );
  const [title, setTitle] = useState(existing?.title ?? '');
  const [until, setUntil] = useState(existing?.contractUntil ?? '');
  const [failure, setFailure] = useState<string | null>(null);

  const typed = Number(percentage.replace(',', '.'));
  /*
   * A mesma conclusão do servidor, repetida onde ela se conserta. Quem manda
   * é `grantRefusalOf`; se as duas divergirem, a frase de lá aparece no
   * alerta.
   */
  const error =
    percentage === ''
      ? undefined
      : !Number.isFinite(typed)
        ? 'Use só números e vírgula — 20 para 20%.'
        : typed <= 0
          ? 'A parte precisa ser maior que zero.'
          : typed >= 100
            ? 'A concessão é uma parte da sua premiação — 100% ou mais deixaria você com zero, ou pagaria mais do que a casa decidiu.'
            : undefined;
  const valid = percentage !== '' && error === undefined;

  const done = () => {
    void client.invalidateQueries({ queryKey: OWN_AWARD_GRANTS_KEY });
    onClose();
  };

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        memberConsultantId: member.id,
        /* Em branco herda o título da premiação — o servidor grava nulo. */
        title: title.trim() === '' ? null : title.trim(),
        percentage: typed,
        /* Vazio é "a ponta da premiação", e o contrato quer nulo, não `''`. */
        contractUntil: until === '' ? null : until,
      };
      const r =
        existing === undefined
          ? await api.sales.createAwardGrant({ params: { id: award.id }, body })
          : await api.sales.editAwardGrant({
              params: { id: award.id, grant: existing.id },
              body: { action: 'editar', ...body },
            });
      if (r.status !== 200 && r.status !== 201)
        throw new Error(refusalOf(r, 'a concessão'));
    },
    onSuccess: done,
    onError: (e: Error) => setFailure(e.message),
  });

  const archive = useMutation({
    mutationFn: async (archived: boolean) => {
      const r = await api.sales.editAwardGrant({
        params: { id: award.id, grant: existing!.id },
        body: { action: 'arquivar', archived },
      });
      if (r.status !== 200) throw new Error(refusalOf(r, 'a concessão'));
    },
    onSuccess: (_, archived) => {
      setFailure(null);
      void client.invalidateQueries({ queryKey: OWN_AWARD_GRANTS_KEY });
      if (archived) onClose();
    },
    onError: (e: Error) => setFailure(e.message),
  });

  if (existing?.archivedAt) {
    return (
      <div className="flex flex-col gap-4">
        {failure && <Alert tone="erro">{failure}</Alert>}
        <p className="flex flex-wrap items-center gap-2 text-sm">
          <Badge tone="neutro">Arquivada</Badge>
          {member.user.nome} tinha {formatPercentage(existing.percentage)}% de{' '}
          {award.title}. Reative para editar — enquanto arquivada, a premiação inteira
          fica com você.
        </p>
        <ModalActions>
          <Button variant="discreto" size="sm" onClick={onClose}>
            Fechar
          </Button>
          <Button
            size="sm"
            loading={archive.isPending}
            onClick={() => archive.mutate(false)}
          >
            Reativar concessão
          </Button>
        </ModalActions>
      </div>
    );
  }

  const leftover = valid ? 100 - typed : null;

  return (
    <div className="flex flex-col gap-5">
      {failure && <Alert tone="erro">{failure}</Alert>}

      <div className="grid grid-cols-1 items-start gap-x-4 gap-y-6 sm:grid-cols-2">
        {/*
         * O nome é o que a pessoa lê no painel, na Carteira e no Extrato dela.
         * Sem ele, a linha dela teria o título da premiação do LÍDER — que é
         * de outra pessoa e conta outra carteira.
         */}
        <div className="sm:col-span-2">
          <Field
            label="Nome da comissão"
            help={`O que ${member.user.nome.split(' ')[0]} lê no painel e no Extrato. Em branco, usa o nome da sua premiação: “${award.title}”.`}
            maxLength={120}
            placeholder={award.title}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <Field
          label="Parte do liderado"
          help={`Em % da sua premiação de ${formatPercentage(award.percentage)}% (${award.title}). 20 é “o liderado fica com 20%, eu com 80%”.`}
          error={error}
          inputMode="decimal"
          value={percentage}
          onChange={(e) => setPercentage(e.target.value.replace(/[^\d.,]/g, ''))}
          required
        />
        <DateField
          label="Contratos até"
          help={
            award.contractUntil === null
              ? 'Em branco: a mesma ponta da premiação, sem limite.'
              : `Em branco: a mesma ponta da premiação, ${award.contractUntil}. Dá para encurtar, não estender.`
          }
          value={until}
          onChange={setUntil}
        />
      </div>

      <p className="text-sm text-texto-suave" aria-live="polite">
        {leftover === null ? (
          <>Informe a parte para ver quanto fica com você.</>
        ) : (
          <>
            Do que a sua premiação pagar sobre as vendas de{' '}
            {member.user.nome.split(' ')[0]},{' '}
            <strong className="font-medium text-texto">
              {formatPercentage(String(typed))}%
            </strong>{' '}
            fica com {member.user.nome.split(' ')[0]} e{' '}
            <strong className="font-medium text-texto">
              {formatPercentage(String(Number(leftover.toFixed(4))))}%
            </strong>{' '}
            com você. Se os {formatPercentage(award.percentage)}% renderem{' '}
            {format(fromReais(100))}, são {format(fromReais(typed))} para{' '}
            {member.user.nome.split(' ')[0]} e {format(fromReais(100 - typed))} para você.
          </>
        )}
      </p>

      <ModalActions className="border-t border-borda-suave pt-4">
        {existing !== undefined && (
          <Button
            variant="discreto"
            size="sm"
            loading={archive.isPending}
            onClick={() => archive.mutate(true)}
          >
            Tirar a concessão
          </Button>
        )}
        <Button variant="discreto" size="sm" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          size="sm"
          disabled={!valid}
          loading={save.isPending}
          onClick={() => save.mutate()}
        >
          {existing === undefined ? 'Conceder' : 'Salvar'}
        </Button>
      </ModalActions>
    </div>
  );
}
